import { Fragment } from 'react';
import clsx from 'clsx';
import { openRefreshReportInNewTab, openUserPortfolioInNewTab } from '../../../../../../utils/utils';
import { DISPATCH_EVENTS } from '../../../../../../utils/constants/commonConstants';
import { buildAddLeadDeepLink } from '../../../../../../utils/searchParamUtils';
import { ACCESS_TIERS } from '../../../../../../store/investor/constants';
import {
    entityHoverHeaderGlowPink,
    entityHoverHeaderGlowPurple,
    globeGray,
    locationPinGray,
    whiteArrowTilted,
} from '../../../../../../assets/hostedassets';
import {
    ENTITY_HOVER_CTA_LABEL,
    ENTITY_HOVER_CTA_START_ICON,
    ENTITY_HOVER_SOURCE,
    FINANCIAL_PLACEHOLDER,
    ENTITY_HOVER_LABELS,
} from './EntityHoverCard.constants';
import type { EntityHoverCta, EntityHoverProfile, HoverStatRow } from './EntityHoverCard.types';
import { PRESELECT_ALL_REFRESH_MODULES_QUERY } from '../../../../dashboardConstants';
import { CompanyLogoBadge } from '../../../../../../common/_custom/CompanyLogoBadge';
import Button from '../../../../../../common/_custom/Button/Button';
import styles from './EntityHoverCard.module.scss';

const PLACEHOLDER_LABELS = [ENTITY_HOVER_LABELS.REVENUE, ENTITY_HOVER_LABELS.PAT, ENTITY_HOVER_LABELS.NET_WORTH];

function StatRow({ row }: { row: HoverStatRow }) {
    return (
        <div className={clsx(styles.StatRow, { [styles.StatRow_Compact]: row.compact, [styles.StatRow_Identifier]: row.identifier })}>
            <span className={styles.StatLabel}>{row.label}</span>
            <span className={styles.StatValueWrap}>
                <span
                    className={clsx(styles.StatValue, {
                        [styles.Pill]: row.asPill,
                        [styles.Tone_Positive]: row.tone === 'positive',
                        [styles.Tone_Negative]: row.tone === 'negative',
                    })}
                >
                    {row.value}
                </span>
                {row.tag && <span className={styles.Tag}>{row.tag}</span>}
            </span>
        </div>
    );
}

function HoverCta({ cta }: { cta: EntityHoverCta }) {
    const onClick = () => {
        document.dispatchEvent(new CustomEvent(DISPATCH_EVENTS.ENTITY_HOVER_CTA_CLICK));
        switch (cta.state) {
            case 'VIEW':
                openUserPortfolioInNewTab(cta.aicaOrgId as string, ACCESS_TIERS.SCAN);
                break;
            case 'UNLOCK':
                openRefreshReportInNewTab(cta.aicaOrgId as string, { [PRESELECT_ALL_REFRESH_MODULES_QUERY]: 'true' });
                break;
            default:
                window.open(
                    buildAddLeadDeepLink(cta.prefillName, cta.prefillIdentifier, ENTITY_HOVER_SOURCE),
                    '_blank',
                    'noopener,noreferrer',
                );
        }
    };
    const startIcon = ENTITY_HOVER_CTA_START_ICON[cta.state];
    return (
        <Button
            type="button"
            variant="purple"
            size="wide"
            className={styles.Cta}
            icon={startIcon && <img src={startIcon} alt="" />}
            endIcon={cta.state === 'VIEW' && <img src={whiteArrowTilted} alt="" className={styles.CtaArrow} />}
            text={ENTITY_HOVER_CTA_LABEL[cta.state]}
            onClick={onClick}
        />
    );
}

function EntityHoverCard({ profile }: { profile: EntityHoverProfile }) {
    const { financials } = profile;
    const hasRelationRows = profile.relationRows.length > 0;
    const hasDetailRows = profile.detailRows.length > 0;
    return (
        <div className={styles.EntityHoverCard}>
            <div className={styles.Band} aria-hidden="true">
                <img src={entityHoverHeaderGlowPink} alt="" className={styles.GlowPink} />
                <img src={entityHoverHeaderGlowPurple} alt="" className={styles.GlowPurple} />
            </div>
            <div className={styles.Header}>
                <CompanyLogoBadge
                    name={profile.name}
                    websiteUrl={profile.websiteHref ?? undefined}
                    size={44}
                    className={styles.Logo}
                    fallback={<span className={clsx(styles.Logo, styles.LogoInitial)}>{profile.name.charAt(0).toUpperCase()}</span>}
                />
                <div className={styles.HeaderText}>
                    <div className={styles.Name} title={profile.name}>
                        {profile.name}
                    </div>
                    {(profile.city || profile.websiteHref) && (
                        <div className={styles.HeaderMeta}>
                            {profile.city && (
                                <span className={styles.MetaItem}>
                                    <img src={locationPinGray} alt="" className={styles.PinIcon} />
                                    {profile.city}
                                </span>
                            )}
                            {profile.websiteHref && (
                                <a className={styles.MetaItem} href={profile.websiteHref} target="_blank" rel="noopener noreferrer">
                                    <img src={globeGray} alt="" className={styles.GlobeIcon} />
                                    <span className={styles.WebsiteText}>{profile.websiteText}</span>
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.Body}>
                {financials && (
                    <>
                        <div className={styles.FinancialsLabel}>
                            <span>{financials.label}</span>
                            <span className={styles.FinancialsRule} />
                        </div>
                        <div className={styles.Tiles}>
                            {(financials.values ?? PLACEHOLDER_LABELS.map((label) => ({ label, value: FINANCIAL_PLACEHOLDER }))).map(
                                ({ label, value }, index) => (
                                    <Fragment key={label}>
                                        {index > 0 && <span className={styles.TileDivider} />}
                                        <div className={styles.Tile}>
                                            <span className={styles.TileLabel}>{label}</span>
                                            <span className={clsx(styles.TileValue, { [styles.TileValue_Blurred]: !financials.values })}>
                                                {value}
                                            </span>
                                        </div>
                                    </Fragment>
                                ),
                            )}
                        </div>
                    </>
                )}
                {hasRelationRows && (
                    <div className={styles.Rows}>
                        {profile.relationRows.map((row) => (
                            <StatRow key={row.label} row={row} />
                        ))}
                    </div>
                )}
                {hasRelationRows && hasDetailRows && <div className={styles.Divider} />}
                {hasDetailRows && (
                    <div className={styles.Rows}>
                        {profile.detailRows.map((row) => (
                            <StatRow key={row.label} row={row} />
                        ))}
                    </div>
                )}
            </div>

            {profile.cta && <HoverCta cta={profile.cta} />}
        </div>
    );
}

export default EntityHoverCard;
